import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Building2,
  Clock,
  DollarSign,
  Download,
  LineChart,
  Percent,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { executiveDashboardService } from '../../services/executiveDashboardService';
import {
  normalizeReportingPeriod,
  reportingPeriodKey,
} from '../../utils/reportingPeriod';
import { useDashboardReportingPeriod } from './hooks/useDashboardReportingPeriod';
import DashboardCommandHeader from './components/DashboardCommandHeader';
import { useDashboardBranding } from './hooks/useDashboardBranding';

const PIE_COLORS = ['#1e40af', '#2563eb', '#3b82f6', '#60a5fa', '#93c5fd'];

const SECTION_PANEL = 'bg-white rounded-lg shadow-sm border border-gray-200';
const KPI_CARD =
  'bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow border-l-4';
const KPI_CARD_INACTIVE = 'bg-gray-50 p-6 rounded-lg shadow-sm border border-dashed border-gray-300 border-l-4 border-l-gray-300';

const KPI_ICONS = {
  net_revenue: DollarSign,
  operating_margin: Percent,
  encounters: Activity,
  net_collection_rate: Percent,
  days_in_ar: Clock,
  denial_rate: AlertTriangle,
  observed_capture: Target,
  ehr_coverage: Building2,
  remit_coverage: Percent,
  cash_on_hand: DollarSign,
  patient_retention: Target,
};

function formatCurrency(value) {
  if (value == null || Number.isNaN(value)) return '—';
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${Math.round(value / 1_000)}K`;
  return `$${Math.round(value).toLocaleString()}`;
}

function TrendChip({ delta, polarity, unit }) {
  if (delta == null) return null;
  const improved =
    polarity === 'lower_is_better' ? delta < 0 : polarity === 'higher_is_better' ? delta > 0 : delta > 0;
  const Icon = improved ? TrendingUp : TrendingDown;
  const color = improved ? 'text-emerald-600' : 'text-red-600';
  const suffix = unit === 'percent' ? '%' : unit === 'days' ? 'd' : '';
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {delta > 0 ? '+' : ''}
      {delta}
      {suffix} vs prior
    </span>
  );
}

function TopKpiTile({ kpi, onOpen }) {
  const Icon = KPI_ICONS[kpi.id] || (kpi.custom ? LineChart : BarChart3);
  const iconColor = kpi.custom ? 'text-indigo-600' : 'text-blue-600';
  const clickable = Boolean(onOpen);

  if (!kpi.live) {
    return (
      <div className={KPI_CARD_INACTIVE}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-600">{kpi.label}</h3>
          <Icon className="w-5 h-5 text-gray-400" />
        </div>
        <p className="text-sm text-gray-500 mt-2">{kpi.integrationLabel || 'Connect a data source to populate this metric'}</p>
        <span className="inline-block mt-3 text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-800">
          Integration required
        </span>
      </div>
    );
  }

  const display =
    kpi.unit === 'percent'
      ? `${Number(kpi.value).toFixed(1)}%`
      : kpi.unit === 'days'
        ? `${Number(kpi.value).toFixed(1)}`
        : kpi.unit === 'currency'
          ? formatCurrency(kpi.value)
          : Number(kpi.value).toLocaleString();

  const accentBorder = kpi.custom ? 'border-l-indigo-500' : 'border-l-blue-500';

  return (
    <div
      className={`${KPI_CARD} ${accentBorder} ${clickable ? 'cursor-pointer' : ''}`}
      onClick={onOpen}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600">{kpi.label}</h3>
        <div className="flex items-center gap-2">
          {kpi.custom && (
            <span className="text-[10px] uppercase tracking-wider text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded">
              Custom
            </span>
          )}
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{kpi.value == null || Number.isNaN(Number(kpi.value)) ? '—' : display}</p>
      {kpi.caption ? <p className="text-xs text-gray-500 mt-1">{kpi.caption}</p> : null}
      <div className="mt-2">
        <TrendChip delta={kpi.delta} polarity={kpi.polarity} unit={kpi.unit} />
      </div>
      {kpi.sourceLabel && kpi.custom && (
        <p className="text-xs text-gray-500 mt-2 truncate" title={kpi.sourceLabel}>
          Source: {kpi.sourceLabel}
        </p>
      )}
      {kpi.sparkline?.length > 0 && (
        <div className="flex items-end gap-0.5 h-6 mt-3">
          {kpi.sparkline.map((v, i) => (
            <div
              key={i}
              className="flex-1 bg-blue-400/70 rounded-sm"
              style={{ height: `${Math.max(20, Math.min(100, v))}%` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Scorecard({ card }) {
  return (
    <div
      className={
        card.live
          ? `${KPI_CARD} border-l-blue-500`
          : `${KPI_CARD_INACTIVE}`
      }
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{card.title}</h3>
        {!card.live && (
          <span className="text-xs font-medium px-2 py-1 rounded bg-amber-100 text-amber-800">
            Pending integration
          </span>
        )}
      </div>
      <dl className="space-y-3">
        {card.metrics.map((m) => (
          <div key={m.label} className="flex justify-between items-start gap-3 text-sm border-b border-gray-100 pb-2 last:border-0 last:pb-0">
            <dt className="text-gray-500">
              {m.label}
              {m.custom && (
                <span className="ml-1 text-[10px] text-indigo-600 uppercase font-medium">custom</span>
              )}
            </dt>
            <dd className={`font-bold text-gray-900 shrink-0 ${m.live ? '' : 'text-gray-400'}`}>{m.value}</dd>
          </div>
        ))}
      </dl>
      {!card.live && card.integrationLabel && (
        <p className="text-xs text-gray-500 mt-3">{card.integrationLabel}</p>
      )}
    </div>
  );
}

function SectionPanel({ icon: Icon, title, subtitle, children, className = '' }) {
  return (
    <div className={`${SECTION_PANEL} ${className}`}>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Icon className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
          </div>
          {subtitle && <span className="text-sm text-gray-500">{subtitle}</span>}
        </div>
        {children}
      </div>
    </div>
  );
}

function ChartPanel({ title, children, className = '' }) {
  return (
    <div className={`${SECTION_PANEL} p-6 ${className}`}>
      <h3 className="text-sm font-medium text-gray-600 mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function ExecutiveDashboard() {
  const navigate = useNavigate();
  const { period, onPeriodChange } = useDashboardReportingPeriod();
  const reportingPeriod = normalizeReportingPeriod(period);
  const { branding, resolvedOrgName, organizationId, orgContextLoading, user } = useDashboardBranding({
    mode: 'executive',
  });

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['executive-dashboard', reportingPeriodKey(reportingPeriod), organizationId, resolvedOrgName],
    queryFn: () =>
      executiveDashboardService.getExecutiveDashboardData({
        period: reportingPeriod,
        organizationName: resolvedOrgName,
        organizationId,
      }),
    staleTime: 120000,
    enabled: !orgContextLoading || !!resolvedOrgName,
  });

  useEffect(() => {
    const onCustomUpdate = () => refetch();
    window.addEventListener('executive-custom-metrics-updated', onCustomUpdate);
    return () => window.removeEventListener('executive-custom-metrics-updated', onCustomUpdate);
  }, [refetch]);

  const benchmarkChartData = useMemo(
    () =>
      (data?.benchmarks || []).map((b) => ({
        name: b.label,
        org: b.live ? b.org : 0,
        fqhc: b.benchmark,
        benchmark: b.benchmark,
      })),
    [data?.benchmarks]
  );

  const trendChartData = useMemo(() => {
    if (!data?.trends?.months) return [];
    return data.trends.months.map((month, i) => ({
      month,
      collections: data.trends.collections[i],
      netRevenue: data.trends.netRevenue[i],
    }));
  }, [data?.trends]);

  const customTrendSeries = useMemo(() => {
    return (data?.customTopKpis || [])
      .filter((k) => k.custom && (k.history?.length >= 2 || k.sparkline?.length >= 2))
      .map((k) => ({
        id: k.id,
        label: k.label,
        unit: k.unit,
        data: (k.history || []).map((h) => ({
          date: typeof h.asOfDate === 'string' ? h.asOfDate.slice(5) : h.asOfDate,
          value: h.value,
        })),
      }));
  }, [data?.customTopKpis]);

  const statusMessage = useMemo(() => {
    const parts = [];
    if (data?.dataStatus === 'partial') {
      parts.push('RCM metrics live; finance/clinical tiles need integrations');
    }
    if (data?.hasCustomMetrics) {
      parts.push('Includes custom practice analytics');
    }
    return parts.join(' · ');
  }, [data?.dataStatus, data?.hasCustomMetrics]);

  return (
    <div className="flex flex-col gap-6">
      <DashboardCommandHeader
        mode="executive"
        branding={data?.branding || branding}
        period={reportingPeriod}
        onPeriodChange={onPeriodChange}
        onRefresh={() => refetch()}
        isFetching={isFetching}
        user={user}
        refreshedAt={data?.refreshedAt}
        statusMessage={statusMessage}
      />

      {isLoading && (
        <div className={`${SECTION_PANEL} p-8 text-center text-gray-500`}>Loading executive KPIs…</div>
      )}

      {!isLoading && data && (
        <>
          <SectionPanel
            icon={BarChart3}
            title="Enterprise KPIs"
            subtitle="Leadership scorecards across revenue cycle and operations"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {data.topKpis.map((kpi) => (
                <TopKpiTile
                  key={kpi.id}
                  kpi={kpi}
                  onOpen={
                    kpi.id === 'net_collection_rate'
                      ? () => navigate('/ar-posting')
                      : kpi.id === 'denial_rate'
                        ? () => navigate('/denials')
                        : undefined
                  }
                />
              ))}
            </div>
          </SectionPanel>

          {customTrendSeries.length > 0 && (
            <SectionPanel
              icon={LineChart}
              title="External metric trends"
              subtitle="Historic values from daily spreadsheet imports"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {customTrendSeries.map((series) => (
                  <div
                    key={series.id}
                    className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 border-l-4 border-l-indigo-500"
                  >
                    <p className="text-sm font-medium text-gray-600 mb-3">{series.label}</p>
                    <div className="h-36">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={series.data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={40} />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#4f46e5"
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            name={series.label}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ))}
              </div>
            </SectionPanel>
          )}

          <SectionPanel icon={Building2} title="Department scorecards" subtitle="Cross-functional performance">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.scorecards.map((card) => (
                <Scorecard key={card.id} card={card} />
              ))}
            </div>
          </SectionPanel>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <ChartPanel title="Financial performance trend" className="xl:col-span-2">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="collections" fill="#93c5fd" name="Collections %" />
                    <Line type="monotone" dataKey="netRevenue" stroke="#1e40af" name="Net revenue index" dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </ChartPanel>

            <div className={`${SECTION_PANEL} p-6`}>
              <h3 className="text-sm font-medium text-gray-600 mb-4">Month summary</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-500">Days in A/R</dt>
                  <dd className="font-bold text-gray-900">
                    {data.monthlySummary.daysInAr != null ? `${data.monthlySummary.daysInAr.toFixed(1)}d` : '—'}
                  </dd>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <dt className="text-gray-500">Net collection rate</dt>
                  <dd className="font-bold text-gray-900">
                    {data.monthlySummary.netCollectionRate != null
                      ? `${data.monthlySummary.netCollectionRate.toFixed(1)}%`
                      : '—'}
                  </dd>
                </div>
                <div className="flex justify-between pb-2">
                  <dt className="text-gray-500">Denial rate</dt>
                  <dd className="font-bold text-gray-900">
                    {data.monthlySummary.denialRate != null ? `${data.monthlySummary.denialRate}%` : '—'}
                  </dd>
                </div>
              </dl>
              <button
                type="button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                onClick={() => navigate('/reporting?service=execReport&report=executive-kpi-snapshot')}
              >
                <Download className="w-4 h-4" />
                Executive report
              </button>
              <button
                type="button"
                className="mt-2 w-full text-sm text-blue-600 hover:underline"
                onClick={() => navigate('/analytics')}
              >
                Open full analytics →
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
            <div className={`${SECTION_PANEL} p-6 lg:col-span-1`}>
              <h3 className="text-sm font-medium text-gray-600 mb-4">Top opportunities</h3>
              <ul className="space-y-3">
                {(data.opportunities || []).map((opp) => (
                  <li key={opp.id} className="text-sm border-b border-gray-100 pb-3 last:border-0">
                    <button
                      type="button"
                      className="text-left w-full hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
                      onClick={() => navigate(opp.link)}
                    >
                      <p className="font-medium text-gray-900">{opp.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{opp.description}</p>
                      <p className="text-xs text-emerald-700 font-medium mt-1">
                        Est. impact: {formatCurrency(opp.estimatedImpact)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <ChartPanel title="Revenue mix (payer)">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.revenueMix} dataKey="value" nameKey="name" innerRadius={40} outerRadius={70}>
                      {data.revenueMix.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </ChartPanel>

            <div className={`${SECTION_PANEL} p-6`}>
              <h3 className="text-sm font-medium text-gray-600 mb-4">Alerts</h3>
              <ul className="space-y-2">
                {data.alerts.map((alert) => (
                  <li
                    key={alert.id}
                    className={`text-xs p-3 rounded-lg flex gap-2 border ${
                      alert.severity === 'high'
                        ? 'bg-red-50 text-red-800 border-red-100'
                        : alert.severity === 'medium'
                          ? 'bg-amber-50 text-amber-900 border-amber-100'
                          : 'bg-gray-50 text-gray-700 border-gray-200'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4 shrink-0" />
                    {alert.message}
                  </li>
                ))}
              </ul>
            </div>

            <ChartPanel title="Benchmarks vs national average">
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={benchmarkChartData} layout="vertical" margin={{ left: 8, right: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="org" fill="#1e40af" name="Your organization" />
                    <Bar dataKey="benchmark" fill="#94a3b8" name="National avg" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </ChartPanel>
          </div>

          <div className={`${SECTION_PANEL} p-6 flex flex-wrap items-center justify-between gap-3`}>
            <div>
              <p className="text-sm font-medium text-gray-900">Revenue cycle deep dive</p>
              <p className="text-xs text-gray-500 mt-0.5">
                ERA, denials, claims, and AR posting analytics remain in service modules — this board summarizes
                executive KPIs.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Remittance', path: '/eob' },
                { label: 'Denials', path: '/denials' },
                { label: 'Claims', path: '/claims' },
                { label: 'AR Posting', path: '/ar-posting' },
              ].map((link) => (
                <button
                  key={link.path}
                  type="button"
                  onClick={() => navigate(link.path)}
                  className="inline-flex items-center gap-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {link.label}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
